import { memo, useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { toast } from 'sonner';
import { Calendar } from '@/components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { TimePicker } from '@/components/ui/time-picker';
import { api } from '../../../convex/_generated/api';
import { ISchedulePublishDialogProps } from '@/@types/scheduledPost';
import { Id } from '../../../convex/_generated/dataModel';

interface IScheduleAccount {
  _id: Id<'connectedSocialAccounts'>;
  status: string;
  accountName: string;
}
import {
  getUserTimezone,
  localDateTimeToUTC,
  getScheduleTimeError,
  formatScheduledTime,
  getTimezoneAbbreviation,
  getCurrentTime,
} from '@/lib/timezone';

const SchedulePublishDialog = ({ postId, isOpen, onClose }: ISchedulePublishDialogProps) => {
  const [selectedAccountIds, setSelectedAccountIds] = useState<Set<Id<'connectedSocialAccounts'>>>(
    new Set()
  );
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedTime, setSelectedTime] = useState(() => getCurrentTime());
  const [isScheduling, setIsScheduling] = useState(false);

  const accounts = useQuery(api.socialMedia.accounts.listConnectedAccounts);
  const createScheduledPost = useMutation(api.socialMedia.scheduledPublishing.createScheduledPost);

  const activeAccounts = useMemo(() => {
    return (accounts || []).filter(
      (account: IScheduleAccount) => account.status === 'active'
    ) as IScheduleAccount[];
  }, [accounts]);

  const hasAccounts = activeAccounts.length > 0;
  const hasSelection = selectedAccountIds.size > 0;
  const hasDateTime = !!selectedDate;
  const canSchedule = hasSelection && hasDateTime;

  const timezone = useMemo(() => getUserTimezone(), []);
  const timezoneAbbr = useMemo(() => getTimezoneAbbreviation(timezone), [timezone]);

  const handleToggleAccount = useCallback((accountId: Id<'connectedSocialAccounts'>) => {
    setSelectedAccountIds(prev => {
      const next = new Set(prev);
      if (next.has(accountId)) {
        next.delete(accountId);
      } else {
        next.add(accountId);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedAccountIds.size === activeAccounts.length) {
      setSelectedAccountIds(new Set());
    } else {
      setSelectedAccountIds(new Set(activeAccounts.map((a: IScheduleAccount) => a._id)));
    }
  }, [activeAccounts, selectedAccountIds.size]);

  const handleSchedule = useCallback(async () => {
    if (!canSchedule || !selectedDate) {
      return;
    }

    setIsScheduling(true);

    try {
      // Convert local date/time to UTC timestamp
      const scheduledFor = localDateTimeToUTC(selectedDate, selectedTime);

      // Validate scheduled time (minimum 1 minute in future)
      const error = getScheduleTimeError(scheduledFor, 1);
      if (error) {
        toast.error(error);
        setIsScheduling(false);
        return;
      }

      await createScheduledPost({
        postId,
        targetAccountIds: Array.from(selectedAccountIds),
        scheduledFor,
        timezone,
      });

      const formattedTime = formatScheduledTime(scheduledFor, timezone);
      toast.success(`Post scheduled for ${formattedTime}`);

      // Reset and close
      setSelectedAccountIds(new Set());
      setSelectedDate(undefined);
      setSelectedTime(getCurrentTime());
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to schedule post');
    } finally {
      setIsScheduling(false);
    }
  }, [
    canSchedule,
    selectedDate,
    selectedTime,
    postId,
    selectedAccountIds,
    timezone,
    createScheduledPost,
    onClose,
  ]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Schedule Post</DialogTitle>
          <DialogDescription>
            Choose when to publish this post to your Facebook Pages
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-6">
          {/* Account Selection */}
          {!hasAccounts && (
            <div className="text-center py-8 text-gray-500">
              <p className="mb-2">No connected Facebook Pages</p>
              <p className="text-sm">
                Connect a Facebook Page first from the Connected Accounts page
              </p>
            </div>
          )}

          {hasAccounts && (
            <>
              <div>
                <div className="flex items-center justify-between mb-4">
                  <Label>Select Facebook Pages</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSelectAll}
                    disabled={isScheduling}
                  >
                    {selectedAccountIds.size === activeAccounts.length
                      ? 'Deselect All'
                      : 'Select All'}
                  </Button>
                </div>

                <div className="space-y-2 max-h-[150px] overflow-y-auto border rounded-lg p-2">
                  {activeAccounts.map(account => (
                    <div
                      key={account._id}
                      role="button"
                      tabIndex={0}
                      className="flex items-center space-x-3 p-2 rounded hover:bg-gray-50 cursor-pointer"
                      onClick={() => handleToggleAccount(account._id)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleToggleAccount(account._id);
                        }
                      }}
                    >
                      <Checkbox
                        checked={selectedAccountIds.has(account._id)}
                        disabled={isScheduling}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{account.accountName}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Date Selection */}
              <div>
                <Label className="mb-2 block">Select Date</Label>
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={date => {
                      // Only disable dates before today (not including today)
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      return date < today;
                    }}
                    className="rounded-md border"
                  />
                </div>
              </div>

              {/* Time Selection */}
              <div>
                <Label className="mb-2 block">Select Time ({timezoneAbbr})</Label>
                <TimePicker
                  value={selectedTime}
                  onChange={setSelectedTime}
                  disabled={isScheduling}
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isScheduling}>
            Cancel
          </Button>
          <Button onClick={handleSchedule} disabled={!canSchedule || isScheduling}>
            {isScheduling
              ? 'Scheduling...'
              : `Schedule for ${selectedAccountIds.size} ${selectedAccountIds.size === 1 ? 'page' : 'pages'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const SchedulePublishDialogMemo = memo(SchedulePublishDialog);
